/**
 * Approvals — first-class app. One place for every committee, CEO, or board
 * sign-off across Core (policies, work plans, attestations, etc.).
 *
 * This is the orchestrator. Three views, no router needed (state-driven):
 *   list    — every package, filterable by artifact type / status / deadline
 *   wizard  — 4-step create flow, optionally pre-populated via initialArtifact
 *   detail  — live status dashboard for one package
 *
 * Deep-linking: the parent (App.js) can pass `initialView` + `initialArtifact`
 * so a "Send for board approval" button from any module (COA, PPM, etc.)
 * can drop the user straight into the wizard with the right thing pre-selected.
 */
import React, { useState, useEffect } from 'react';
import ApprovalsHome from './ApprovalsHome';
import PackageWizard from './PackageWizard';
import PackageDetail from './PackageDetail';
import BoardTokensAdmin from './BoardTokensAdmin';

export default function ApprovalsApp({ initialView = 'list', initialArtifact = null, onConsumeInitial }) {
  const [view, setView] = useState(initialView === 'wizard' ? 'wizard' : 'list');
  const [packageId, setPackageId] = useState(null);
  const [seededArtifact, setSeededArtifact] = useState(initialArtifact);

  // If the parent passed an initial deep-link payload, take it and clear so
  // the same payload doesn't keep re-seeding the wizard on re-render.
  useEffect(() => {
    if (initialArtifact && onConsumeInitial) onConsumeInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenPackage  = (id) => { setPackageId(id); setView('detail'); };
  const handleCreate       = () => { setSeededArtifact(null); setPackageId(null); setView('wizard'); };
  const handleBack         = () => { setPackageId(null); setSeededArtifact(null); setView('list'); };
  const handleCreated      = (id) => { setPackageId(id); setSeededArtifact(null); setView('detail'); };

  return (
    <div className="h-full flex flex-col bg-[#f8fafc] min-w-0 min-h-0">
      <header className="flex-shrink-0 bg-white border-b border-slate-200/60">
        <div className="px-8 pt-6 pb-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/25 flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">SignoffHub</h1>
              <p className="text-slate-400 text-sm mt-0.5">Send policies, work plans, attestations, risk assessments, and more for committee, CEO, or board sign-off — with an audit trail that's done before they finish signing.</p>
            </div>
          </div>
          {view === 'list' && (
            <button
              onClick={() => setView('board-admin')}
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
              title="Issue or revoke long-lived links for board members to see their own approval history."
            >
              🔗 Board links
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 overflow-auto">
        {view === 'list' && (
          <ApprovalsHome
            onOpenPackage={handleOpenPackage}
            onCreatePackage={handleCreate}
          />
        )}
        {view === 'wizard' && (
          <PackageWizard
            onCancel={handleBack}
            onCreated={handleCreated}
            seededArtifact={seededArtifact}
          />
        )}
        {view === 'detail' && packageId && (
          <PackageDetail packageId={packageId} onBack={handleBack} />
        )}
        {view === 'board-admin' && (
          <BoardTokensAdmin onBack={() => setView('list')} />
        )}
      </main>
    </div>
  );
}
